import React, { useState } from "react";
import styled from "styled-components";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../styles/theme";
import { Button } from "../ui/Button";
import { Input, Label, ErrorMessage, FormGroup } from "../ui/Input";
import { Card } from "../ui/Card";
import logo from "../../assets/logo.jpg";

const Screen = styled.div`
  min-height: 100vh;
  padding: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.background.canvas};
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const LogoLockup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const LogoMark = styled.img`
  width: 44px;
  height: 44px;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.stroke.subtle};
  object-fit: cover;
`;

const TitleStack = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
`;

const Caption = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
`;

const FormCard = styled(Card)`
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.background.secondary};
  border: ${theme.borders.base};
`;

function LoginScreen() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!email || !password) {
      setError("Please fill in your email and password.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await signIn(email, password);
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case "auth/user-not-found":
      case "auth/wrong-password":
        return "Invalid email or password";
      case "auth/invalid-email":
        return "Invalid email address";
      case "auth/user-disabled":
        return "This account has been disabled";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later";
      default:
        return "Failed to sign in. Please try again";
    }
  };

  return (
    <Screen>
      <FormContainer>
        <Header>
          <LogoLockup>
            <LogoMark src={logo} alt="CopyUGC logo" />
            <TitleStack>
              <Title>CopyUGC</Title>
              <Caption>Great Artists Steal</Caption>
            </TitleStack>
          </LogoLockup>
        </Header>

        <FormCard>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@team.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={error && !email}
                disabled={loading}
                autoFocus
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                error={error && !password}
                disabled={loading}
              />
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Button
              type="submit"
              fullWidth
              disabled={loading}
            >
              {loading ? "Signing in..." : "Login"}
            </Button>
          </form>
        </FormCard>
      </FormContainer>
    </Screen>
  );
}

export default LoginScreen;


